import { computed, toValue } from "vue";

export const MAX_RATING = 5;

/**
 * The glyph for each star position of a rating out of `MAX_RATING`: `"full"`, `"half"` or `"empty"`.
 * The rating rounds to the nearest half, so 4.3 draws four full stars and a half star.
 * Empty positions are left out unless `empty` is set, and an unrated (zero) rating draws no stars.
 *
 * @param {import("vue").MaybeRefOrGetter<number>} rating The rating, already clamped to 0–`MAX_RATING`.
 * @param {{ empty?: import("vue").MaybeRefOrGetter<boolean> }} [options]
 * @returns {import("vue").ComputedRef<Array<"full" | "half" | "empty">>}
 */
export function useStars(rating, { empty = false } = {}) {
  return computed(() => {
    const value = toValue(rating);

    if (!value) {
      return [];
    }

    const rounded = Math.round(value * 2) / 2;
    const glyphs = Array.from({ length: MAX_RATING }, (_, index) => starAt(rounded, index + 1));

    return toValue(empty) ? glyphs : glyphs.filter((glyph) => glyph !== "empty");
  });
}

function starAt(rounded, position) {
  if (rounded >= position) {
    return "full";
  }

  return position - rounded === 0.5 ? "half" : "empty";
}
